import { z } from 'zod'
import type { TFunction } from '@mc/i18n'

/** Matches backend `EnrollmentApplication::WRITTEN_SUBMISSION_WORD_LIMIT`. */
export const WRITTEN_SUBMISSION_MAX_WORDS = 400

/** Control characters except tab, LF, and CR. */
const DISALLOWED_CONTROL_CHARS =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/

const HTML_DELIMITERS = /[<>]/

/** Encoded markup (e.g. &lt;, &#60;, &#x3c;) */
const HTML_ENTITY_ENCODING = /&(?:#\d+|#x[\da-f]+|\w{2,8});/i

const TEXT_INJECTION_PATTERNS: RegExp[] = [
  /<\s*\/?\s*script\b/i,
  /<\s*iframe\b/i,
  /<\s*img\b/i,
  /<\s*svg\b/i,
  /<\s*object\b/i,
  /<\s*embed\b/i,
  /<\s*link\b/i,
  /<\s*style\b/i,
  /\bjavascript\s*:/i,
  /\bvbscript\s*:/i,
  /\bdata\s*:\s*text\/html/i,
  /<[^>]*\bon[a-z]+\s*=/i,
  /\\u003[cC]/,
  /\\x3[cC]/,
  /%3[cC]/i,
]

/**
 * Client-side guard for plain-text fields (e.g. written submission essays).
 * Blocks HTML/script injection markers; output encoding on display remains required.
 */
export const containsUnsafeSubmissionText = (value: string): boolean => {
  if (DISALLOWED_CONTROL_CHARS.test(value)) return true
  if (HTML_DELIMITERS.test(value)) return true
  if (HTML_ENTITY_ENCODING.test(value)) return true
  return TEXT_INJECTION_PATTERNS.some((pattern) => pattern.test(value))
}

/**
 * Word count aligned with backend `written_submission.to_s.scan(/\S+/).size`.
 */
export const countWords = (value: string): number => {
  const trimmed = value.trim()
  if (trimmed === '') return 0
  return trimmed.split(/\s+/).length
}

/**
 * Shared email validation schema.
 * Used consistently across email-entry, login, and register flows.
 */
export const emailSchema = z.string().email()

/**
 * Validates an email string and returns the result.
 * @param email - The email string to validate
 * @returns true if valid, false otherwise
 */
export const isValidEmail = (email: string): boolean =>
  emailSchema.safeParse(email).success

/**
 * Characters allowed in a person's name: letters, combining marks, whitespace,
 * and the punctuation common in real names — apostrophe, period, comma, hyphen.
 * Single source of truth shared by `createFullNameSchema` and
 * `hasInvalidNameChars` so the two never drift apart.
 */
const NAME_CHAR_CLASS = String.raw`\p{L}\p{M}\s'.,\-`

/** Matches a single character that isn't allowed in a name. */
const INVALID_NAME_CHAR = new RegExp(`[^${NAME_CHAR_CLASS}]`, 'u')

/** Matches a non-empty string composed entirely of allowed name characters. */
const FULL_NAME_PATTERN = new RegExp(`^[${NAME_CHAR_CLASS}]+$`, 'u')

/**
 * True when `value` contains a character that isn't valid in a name. Independent
 * of emptiness — an empty string reports no invalid characters.
 */
export const hasInvalidNameChars = (value: string): boolean =>
  INVALID_NAME_CHAR.test(value)

/**
 * True when `value` contains both a first and last name — i.e. at least two
 * whitespace-separated parts. Mirrors the backend, which splits the full name
 * on whitespace and requires first and last name to both be present before an
 * enrollment application can be submitted (returns `incomplete_user_profile`
 * otherwise).
 */
export const hasFirstAndLastName = (value: string): boolean =>
  value.trim().split(/\s+/).filter(Boolean).length >= 2

/**
 * Full Name validation schema factory.
 * Accepts: Letters (including accented characters like José, Müller), spaces, hyphens, apostrophes, periods, commas
 * Rejects: Numbers and special characters
 *
 * @param t - Translation function for error messages
 * @param errorKey - Optional custom error message key (defaults to 'enrollment.application.form.errors.invalidFullName')
 * @returns Zod schema for full name validation
 *
 * @example
 * const schema = createFullNameSchema(t)
 * // Valid: "John Doe", "Mary-Jane", "O'Brien", "J.K. Rowling", "José García", "Smith, Jr."
 * // Invalid: "John123", "User@Name"
 */
export const createFullNameSchema = (
  t: TFunction,
  errorKey = 'enrollment.application.form.errors.invalidFullName',
) =>
  z
    .string()
    .min(1, t('enrollment.application.form.errors.required'))
    .regex(FULL_NAME_PATTERN, t(errorKey))

/**
 * LinkedIn URL validation schema factory.
 * Accepts URLs with or without protocol (http:// or https://)
 * Validates that it's a proper LinkedIn profile URL
 *
 * Supports multiple formats:
 * - /in/ paths: linkedin.com/in/username
 * - /pub/ paths: linkedin.com/pub/name/1/2/3
 * - Country subdomains: uk.linkedin.com, nl.linkedin.com, etc.
 * - www subdomain: www.linkedin.com
 *
 * @param value - The URL string to validate
 * @returns true if valid (or empty), false otherwise
 *
 * @example
 * isValidLinkedInProfileUrl('linkedin.com/in/username') // true
 * isValidLinkedInProfileUrl('uk.linkedin.com/pub/name/1/2/3') // true
 * isValidLinkedInProfileUrl('facebook.com/user') // false
 *
 * Pattern based on: https://stackoverflow.com/a/19289494
 * License: CC BY-SA 3.0
 */
/**
 * LinkedIn vanity name (the path segment after /in/ or /pub/): letters,
 * numbers, dot, underscore, hyphen, up to LinkedIn's 100-character
 * custom-URL limit. Tested against the decoded segment so percent-encoded
 * whitespace and symbols can't sneak through.
 */
const LINKEDIN_VANITY_NAME_PATTERN = /^[\p{L}\p{N}._-]{1,100}$/u

const isValidLinkedInVanityName = (pathSegment: string): boolean => {
  try {
    return LINKEDIN_VANITY_NAME_PATTERN.test(decodeURIComponent(pathSegment))
  } catch {
    return false
  }
}

export const isValidLinkedInProfileUrl = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed) return true

  // A URL never contains literal whitespace — `new URL` silently
  // percent-encodes it, which once let essay text pasted after a profile URL
  // pass validation.
  if (/\s/.test(trimmed)) return false

  try {
    let urlString = trimmed
    if (!urlString.startsWith('http://') && !urlString.startsWith('https://')) {
      urlString = `https://${urlString}`
    }

    const url = new URL(urlString)

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return false
    }

    const hostnamePattern = /^(www|\w{2})\.linkedin\.com$|^linkedin\.com$/
    if (!hostnamePattern.test(url.hostname)) {
      return false
    }

    const inMatch = url.pathname.match(/^\/in\/([^/]+)\/?$/)
    if (inMatch?.[1]) {
      return isValidLinkedInVanityName(inMatch[1])
    }

    const pubMatch = url.pathname.match(/^\/pub\/([^/]+)\/\w+\/\w+\/\w+\/?$/)
    if (pubMatch?.[1]) {
      return isValidLinkedInVanityName(pubMatch[1])
    }

    return false
  } catch {
    return false
  }
}

/**
 * Essay body for written submission — required, no angle brackets, max word count.
 */
export const createWrittenSubmissionEssaySchema = (
  t: TFunction,
  {
    requiredKey = 'enrollment.status.steps.writtenSubmission.modal.errors.essayRequired',
    invalidCharactersKey = 'enrollment.status.steps.writtenSubmission.modal.errors.invalidCharacters',
    maxWordsKey = 'enrollment.status.steps.writtenSubmission.modal.errors.maxWords',
    maxWords = WRITTEN_SUBMISSION_MAX_WORDS,
  }: {
    requiredKey?: string
    invalidCharactersKey?: string
    maxWordsKey?: string
    maxWords?: number
  } = {},
) =>
  z
    .string()
    .refine((value) => countWords(value) > 0, {
      message: t(requiredKey),
    })
    .refine((value) => !containsUnsafeSubmissionText(value), {
      message: t(invalidCharactersKey),
    })
    .refine((value) => countWords(value) <= maxWords, {
      message: t(maxWordsKey, { max: maxWords }),
    })

/**
 * Normalizes a LinkedIn URL to always use https://
 * Used before sending data to the backend to ensure consistent URL format
 *
 * @param url - The LinkedIn URL to normalize
 * @returns Normalized URL with https:// protocol
 *
 * @example
 * normalizeLinkedInUrl('linkedin.com/in/user') // 'https://linkedin.com/in/user'
 * normalizeLinkedInUrl('http://linkedin.com/in/user') // 'https://linkedin.com/in/user'
 */
export const normalizeLinkedInUrl = (url: string): string => {
  let normalized = url.trim()

  // Add https:// if no protocol
  if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
    normalized = `https://${normalized}`
  }

  // Convert http:// to https://
  if (normalized.startsWith('http://')) {
    normalized = normalized.replace('http://', 'https://')
  }

  return normalized
}
